import { ApolloClient, InMemoryCache, createHttpLink, from } from '@apollo/client';
import { onError } from '@apollo/client/link/error';
import { handleApiError } from '../errorHandlers';
import { createLogger } from '../logError';
import { userVar } from './localState';
import { setContext } from '@apollo/client/link/context';
import { buildSessionHeaders } from 'features/auth/session';

const logger = createLogger('Apollo');

// Get GraphQL endpoint from environment variable or use default
const getGraphQLEndpoint = () => {
  if (process.env.REACT_APP_GRAPHQL_URL) {
    return process.env.REACT_APP_GRAPHQL_URL;
  }
  return '/graphql';
};

const GRAPHQL_URI = getGraphQLEndpoint();
logger.log(`[Apollo] Using GraphQL endpoint: ${GRAPHQL_URI}`);

// Global error handling link with better error messages
const errorLink = onError(({ graphQLErrors, networkError, operation, forward }) => {
  if (graphQLErrors) {
    graphQLErrors.forEach(err => {
      logger.error('[GraphQL error]', {
        message: err.message,
        locations: err.locations,
        path: err.path,
        extensions: err.extensions,
        operation: operation?.operationName,
      });
    });
  }
  
  if (networkError) {
    const networkErr = networkError as any;
    
    // Check for JSON parse errors (empty or incomplete responses)
    const isJsonParseError = networkError.message?.includes('JSON Parse error') || 
                           networkError.message?.includes('Unexpected EOF') ||
                           networkError.message?.includes('Unexpected end of JSON input');
    
    if (isJsonParseError) {
      logger.error('[JSON Parse error] - Empty or incomplete response', {
        message: networkError.message,
        statusCode: networkErr.statusCode,
        operation: operation?.operationName,
        endpoint: GRAPHQL_URI,
      });
      
      // Enhance the error message for JSON parse errors
      networkError.message = 'Server returned an empty or incomplete response. Please check your connection and try again.';
    } else {
      logger.error('[Network error]', {
        message: networkError.message,
        statusCode: networkErr.statusCode,
        result: networkErr.result,
        operation: operation?.operationName,
        endpoint: GRAPHQL_URI,
      });
    }
    
    // Log helpful debugging info for production issues
    if (typeof window !== 'undefined') {
      logger.error('[Network error details]', {
        currentUrl: window.location.href,
        graphqlEndpoint: GRAPHQL_URI,
        hasToken: !!localStorage.getItem('access_token'),
      });
    }
  }
});

const httpLink = createHttpLink({
  uri: GRAPHQL_URI,
  credentials: 'include',
});

const authLink = setContext((_, { headers }) => {
  const token =
    typeof window !== 'undefined'
      ? localStorage.getItem('access_token')
      : null;
  return {
    headers: buildSessionHeaders({
      ...(headers as Record<string, string>),
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
    }),
  };
});

export const client = new ApolloClient({
  link: from([errorLink, authLink, httpLink]),
  cache: new InMemoryCache({
    typePolicies: {
      Idea: { keyFields: ['ideaId'] },
      IdeaSummary: { keyFields: ['ideaId'] },
    },
  }),
  defaultOptions: {
    watchQuery: {
      fetchPolicy: 'cache-first',
      nextFetchPolicy: 'cache-first',
      // Polling queries (messaging, invitations, nav badge) skip their tick
      // while the tab is hidden — background tabs otherwise keep hitting
      // /graphql every 15-120s per the polling inventory in
      // docs/PERFORMANCE_MIGRATION_NOTES.md.
      skipPollAttempt: () =>
        typeof document !== 'undefined' && document.visibilityState === 'hidden',
    },
    query: {
      fetchPolicy: 'cache-first',
    },
  },
}); 